import { createClient, type Client, type ExecutionResult } from 'graphql-ws';

export type WsInitConfig = {
	endpoint: string;
	token_provider: () => Promise<string | null>;
	webSocketImpl?: typeof WebSocket;
	heartbeat_interval_ms?: number;
	pong_timeout_ms?: number;
};

let init: WsInitConfig | null = null;
let warned_missing_endpoint = false;

export function init_ws(config: WsInitConfig) {
	init = config;
	if (!config.endpoint && !warned_missing_endpoint) {
		warned_missing_endpoint = true;
		console.warn('init_ws: endpoint missing — subscriptions will no-op until configured');
	}
}

let client: Client | null = null;
let refcount = 0;
let warned_subscribe_before_init = false;

export type WsHandlers = {
	next: (result: { data?: any; errors?: unknown[] }) => void;
	error?: (error: unknown) => void;
	complete?: () => void;
};

function attach_heartbeat(socket: any, cfg: WsInitConfig) {
	const heartbeat_interval_ms = cfg.heartbeat_interval_ms ?? 30000;
	const pong_timeout_ms = cfg.pong_timeout_ms ?? 7000;

	let pong_timeout: ReturnType<typeof setTimeout> | undefined;

	const heartbeat_interval = setInterval(() => {
		socket.send(JSON.stringify({ type: 'ping' }));
		pong_timeout = setTimeout(() => {
			console.error('pong_timeout, closing socket');
			socket.close(4408, 'Pong timeout');
		}, pong_timeout_ms);
	}, heartbeat_interval_ms);

	function pong_handler(event: MessageEvent) {
		try {
			const msg = JSON.parse(event.data);
			if (msg.type === 'pong') {
				if (pong_timeout) {
					clearTimeout(pong_timeout);
					pong_timeout = undefined;
				}
			}
		} catch {
			console.error('error at pong_handler', event?.data);
		}
	}

	socket.addEventListener('message', pong_handler);
	socket.addEventListener('close', () => {
		clearInterval(heartbeat_interval);
		if (pong_timeout) clearTimeout(pong_timeout);
		socket.removeEventListener('message', pong_handler);
	});
}

function ensure_client(): Client {
	if (client) return client;
	if (!init) throw new Error('ws not initialized');
	const cfg = init;
	client = createClient({
		url: cfg.endpoint,
		webSocketImpl: cfg.webSocketImpl,
		shouldRetry: () => true,
		retryAttempts: Infinity,
		retryWait: (attempts) => new Promise((resolve) => setTimeout(resolve, Math.min(1000 * 2 ** attempts, 30000))),
		connectionParams: async () => ({
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await cfg.token_provider()}`
			}
		}),
		lazy: true,
		lazyCloseTimeout: 0,
		on: {
			connected: (socket: any) => attach_heartbeat(socket, cfg)
		}
	});
	return client;
}

export function ws_subscribe(
	op: { query: string; variables?: Record<string, unknown> },
	handlers: WsHandlers
): () => void {
	if (!init) {
		// A6: warn once instead of silently no-oping — a subscription before
		// init_ws is a wiring bug, not a supported path.
		if (!warned_subscribe_before_init) {
			warned_subscribe_before_init = true;
			console.warn('ws_subscribe called before init_ws — subscription no-op');
		}
		return () => {};
	}
	refcount += 1;

	let dispose: (() => void) | null = null;
	try {
		const active_client = ensure_client();
		dispose = active_client.subscribe(
			{ query: op.query, variables: op.variables },
			{
				next: (result: ExecutionResult) =>
					handlers.next(result as { data?: unknown; errors?: unknown[] }),
				error: (error) => handlers.error?.(error),
				complete: () => handlers.complete?.()
			}
		);
	} catch (error) {
		handlers.error?.(error);
	}

	let done = false;
	return () => {
		if (done) return;
		done = true;
		if (dispose) dispose();
		refcount -= 1;
		if (refcount <= 0) teardown();
	};
}

export function ws_refcount(): number {
	return refcount;
}

export function ws_has_client(): boolean {
	return client !== null;
}

export function close_ws() {
	refcount = 0;
	teardown();
}

function teardown() {
	if (client) {
		client.terminate();
		client = null;
	}
}
